import React, { useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import SelectBox from '../../../../atomic/selectbox/SelectBox';
import { SectionHeader } from '../components/SectionHeader';
import { SectionInfoBanner } from '../components/SectionInfoBanner';
import { StoreTabFooter } from '../components/StoreTabFooter';
import { SS_OUTLINED_INPUT, ssFieldSx, grid2, WEEKDAY_OPTIONS } from '../storeSettingsConstants';
import { normalizeTimeTo12Hour } from '../storeSettingsForm';

dayjs.extend(customParseFormat);

const parsePickerValue = (value) => {
  const normalized = normalizeTimeTo12Hour(value);
  if (!normalized) return null;
  const parsed = dayjs(normalized, 'hh:mm A');
  return parsed.isValid() ? parsed : null;
};

const formatPickerValue = (value) => {
  if (!value || !value.isValid?.()) return '';
  return value.format('hh:mm A');
};

const isValidTimeInput = (value) => {
  if (!value) return true;
  return normalizeTimeTo12Hour(value) !== '';
};

export function HoursRestrictedSection({ meta, form, updateField, tabFooterProps }) {
  const openingTimeError = !isValidTimeInput(form.operation_start_time);
  const closingTimeError = !isValidTimeInput(form.operation_end_time);
  const [openStartPicker, setOpenStartPicker] = useState(false);
  const [openEndPicker, setOpenEndPicker] = useState(false);

  const timePickerDropdownSx = {
    '& .MuiPaper-root': {
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
    },
    '& .MuiMultiSectionDigitalClockSection-item': {
      color: '#3F3F6E',
      fontSize: '14px',
      fontWeight: 600,
      borderRadius: '8px',
      mx: '4px',
      my: '2px',
    },
    '& .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
      backgroundColor: '#fdeee7',
      color: '#3F3F6E',
    },
    '& .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover': {
      backgroundColor: '#fcddcf',
    },
    '& .MuiMultiSectionDigitalClockSection-item:hover': {
      backgroundColor: '#fff4ef',
    },
  };

  return (
    <>
      <SectionHeader Icon={meta.Icon} title={meta.title} subtitle={meta.subtitle} />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div style={{ ...grid2, marginBottom: 18 }}>
          <TimePicker
            open={openStartPicker}
            onOpen={() => setOpenStartPicker(true)}
            onClose={() => setOpenStartPicker(false)}
            value={parsePickerValue(form.operation_start_time)}
            onChange={(value) => updateField('operation_start_time', formatPickerValue(value))}
            label='Opening time'
            ampm
            openTo='hours'
            views={['hours', 'minutes']}
            slots={{}}
            slotProps={{
              popper: { sx: timePickerDropdownSx },
              textField: {
                id: 'operation_start_time',
                className: SS_OUTLINED_INPUT,
                size: 'small',
                variant: 'outlined',
                placeholder: 'e.g. 09:00 AM',
                error: openingTimeError,
                helperText: openingTimeError ? 'Use HH:MM AM/PM (example: 09:00 AM)' : ' ',
                sx: ssFieldSx,
                onClick: () => setOpenStartPicker(true),
              },
            }}
          />
          <TimePicker
            open={openEndPicker}
            onOpen={() => setOpenEndPicker(true)}
            onClose={() => setOpenEndPicker(false)}
            value={parsePickerValue(form.operation_end_time)}
            onChange={(value) => updateField('operation_end_time', formatPickerValue(value))}
            label='Closing time'
            ampm
            openTo='hours'
            views={['hours', 'minutes']}
            slots={{}}
            slotProps={{
              popper: { sx: timePickerDropdownSx },
              textField: {
                id: 'operation_end_time',
                className: SS_OUTLINED_INPUT,
                size: 'small',
                variant: 'outlined',
                placeholder: 'e.g. 10:00 PM',
                error: closingTimeError,
                helperText: closingTimeError ? 'Use HH:MM AM/PM (example: 10:00 PM)' : ' ',
                sx: ssFieldSx,
                onClick: () => setOpenEndPicker(true),
              },
            }}
          />
        </div>
      </LocalizationProvider>

      {/** “We will enable this feature later. It will be tested on the customer side before being released to kitchens.” */}
      {/* <div style={{ marginBottom: 8 }}>
        <SelectBox
          sx={{ width: '100%' }}
          label='No delivery on (optional)'
          value={form.restrictedDeliveryDay}
          onChange={(value) => updateField('restrictedDeliveryDay', value)}
          options={WEEKDAY_OPTIONS.map((o) => o.value)}
          formatOptionLabel={(v) => WEEKDAY_OPTIONS.find((o) => o.value === v)?.label ?? v}
        />
        <p className='store-settings-helper-text'>
          Pick a weekday when you do not offer delivery, or leave as None.
        </p>
      </div> */}

      <StoreTabFooter tabId='2' {...tabFooterProps} />
    </>
  );
}
